import {createAsyncThunk} from "@reduxjs/toolkit";
import type {Dealership, DealerUser} from "@/types";
import type {RootState} from "@/store";
import {supabase} from "@/utils/supabase.ts";
import {
    generateId,
    keysToCamelCase,
    keysToSnakeCase,
    makeFileName,
    normalizeError,
    prepareImageFile,
    uploadToR2
} from "@/utils";

type CreateDealerPayload = {
    dealer: Omit<Dealership, "id">;
    dealerUser: Omit<DealerUser, "id" | "dealerId">;
};

type CreateDealerResult = {
    dealer: Dealership;
    dealerUser: DealerUser;
};

const DEALER_MEDIA_BUCKET = "dealer-media";


async function uploadImageFromSource(
    source: string | undefined,
    path: string,
    filename: string,
    options: {
        maxSizeMB: number;
        maxWidthOrHeight: number;
    }
) {
    if (!source) return undefined;

    if (!source.startsWith("blob:")) {
        return source;
    }

    const compressedFile = await prepareImageFile(source, filename, options);
    const storagePath = `${path}/${filename}`;
    return uploadToR2(DEALER_MEDIA_BUCKET, storagePath, compressedFile, "image/webp");
}

async function uploadGalleryImages(images: string[], dealerId: string) {
    const uploaded = await Promise.all(
        images.map((image, index) =>
            uploadImageFromSource(
                image,
                `${dealerId}/gallery`,
                makeFileName(`gallery_${index}`, `gallery_${index}.webp`),
                {
                    maxSizeMB: 0.6,
                    maxWidthOrHeight: 1600
                }
            )
        )
    );

    return uploaded.filter((image): image is string => Boolean(image));
}

export const createDealer = createAsyncThunk<CreateDealerResult, CreateDealerPayload, {rejectValue: string}>(
    "authentication/createDealer",
    async ({dealer, dealerUser}, {rejectWithValue, getState}) => {
        const {authentication} = getState() as RootState;
        const stateUserId = authentication.dealerUser?.id;

        const {data, error} = await supabase.auth.getUser();
        if (error) return rejectWithValue(error.message);

        const authUserId = data.user?.id;
        if (!authUserId) {
            return rejectWithValue("Session expired. Please log in again.");
        }

        if (stateUserId && stateUserId !== authUserId) {
            return rejectWithValue("Auth mismatch detected.");
        }

        try {
            const existingDealerId = authentication.dealerUser?.dealerId ?? authentication.user?.id;
            const dealerId = existingDealerId ?? generateId();
            const uploadedProfile = await uploadImageFromSource(
                dealer.profile,
                `${dealerId}/profile`,
                makeFileName("profile", "profile.webp"),
                {
                    maxSizeMB: 0.08,
                    maxWidthOrHeight: 320
                }
            );

            const uploadedBanner = await uploadImageFromSource(
                dealer.banner,
                `${dealerId}/banner`,
                makeFileName("banner", "banner.webp"),
                {
                    maxSizeMB: 0.4,
                    maxWidthOrHeight: 1400
                }
            );

            const uploadedUserProfile = await uploadImageFromSource(
                dealerUser.profile,
                `${authUserId}/user-profile`,
                makeFileName("user_profile", "user_profile.webp"),
                {
                    maxSizeMB: 0.08,
                    maxWidthOrHeight: 320
                }
            );

            const uploadedImages = await uploadGalleryImages(dealer.images ?? [], dealerId);

            const dealershipInsert = keysToSnakeCase({
                ...dealer,
                id: dealerId,
                profile: uploadedProfile,
                banner: uploadedBanner,
                images: uploadedImages
            });

            const dealershipQuery = existingDealerId
                ? supabase
                    .from("dealerships")
                    .update(dealershipInsert)
                    .eq("id", dealerId)
                : supabase
                    .from("dealerships")
                    .insert(dealershipInsert);

            const {data: dealershipData, error: dealershipError} = await dealershipQuery
                .select("*")
                .single();

            if (dealershipError) {
                return rejectWithValue(dealershipError.message);
            }

            const dealerUserInsert = keysToSnakeCase({
                ...dealerUser,
                id: authUserId,
                dealerId: dealerId,
                profile: uploadedUserProfile
            });

            const {data: dealerUserData, error: dealerUserError} = await supabase
                .from("dealer_users")
                .upsert(dealerUserInsert, {onConflict: "id"})
                .select("*")
                .single();

            if (dealerUserError) {
                return rejectWithValue(dealerUserError.message);
            }

            return {
                dealer: keysToCamelCase<Dealership>(dealershipData),
                dealerUser: keysToCamelCase<DealerUser>(dealerUserData)
            };
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to create dealer"));
        }
    }
);
